import { clsx } from 'clsx';
import {
  type ComponentPropsWithRef,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useCssCustomProperty } from '@repo/dom/hooks/useCssCustomProperty';
import { Bleed } from '@repo/ui/Bleed';
import { Flex } from '@repo/ui/Flex';
import { IconButton } from '@repo/ui/IconButton';

import s from './Toast.module.css';

const DEFAULT_TIMER = 3000;

export const toastVariantOptions = ['error', 'info', 'success'] as const;
export type ToastVariant = (typeof toastVariantOptions)[number];

export const toastPositionOptions = [
  'topLeft',
  'topCenter',
  'topRight',
  'bottomRight',
  'bottomCenter',
  'bottomLeft',
] as const;
export type ToastPosition = (typeof toastPositionOptions)[number];

export interface ToastProps extends ComponentPropsWithRef<'div'> {
  action?: ReactNode;
  autoClose?: boolean;
  children?: ReactNode;
  className?: string;
  duration?: number;
  isDismissible?: boolean;
  onClose?: () => void;
  position?: ToastPosition;
  variant?: ToastVariant;
}

export const Toast = (props: ToastProps) => {
  const {
    action,
    // Action toasts persist by default so users have time to act (WCAG 2.2.1)
    autoClose = !action,
    children,
    className,
    duration = DEFAULT_TIMER,
    isDismissible = true,
    onClose,
    variant = 'info',
    ...attrs
  } = props;

  const animationDuration =
    parseInt(useCssCustomProperty('--n-toast-animation-duration-ms'), 10) ??
    undefined;
  const [isExiting, setIsExiting] = useState(false);

  // Store timer references in refs to ensure they're properly cleared
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Set up timers on mount, not on re-render
  useEffect(() => {
    if (!autoClose || !animationDuration) return;

    // Function to handle the complete close lifecycle
    const startTimers = () => {
      timerRef.current = setTimeout(() => {
        setIsExiting(true);
        exitTimerRef.current = setTimeout(() => {
          onClose?.();
        }, animationDuration);
      }, duration);
    };

    startTimers();

    // Cleanup function to clear all timers
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
    // eslint-disable-next-line exhaustive-deps
  }, [animationDuration]); // We need to run this affect once we know the animation duration.

  const showCloseButton = isDismissible || !autoClose;

  const handleClose = () => {
    // Clear any existing timers first
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    exitTimerRef.current = null;

    setIsExiting(true);
    exitTimerRef.current = setTimeout(() => {
      onClose?.();
    }, animationDuration);
  };

  return (
    <div
      className={clsx(s.toast, isExiting && s.isExiting, className)}
      data-testid={`toast-${variant}`}
      data-variant={variant}
      role="alert"
      {...attrs}
    >
      <div className={clsx(s.wrap)}>
        <Flex
          alignItems="center"
          className={s.body}
          display="inline-flex"
          justifyContent="space-between"
        >
          <div className={s.content}>{children}</div>
          {(action || showCloseButton) && (
            <Flex
              display="inline-flex"
              alignItems="center"
              className={s.actions}
            >
              {action}
              {showCloseButton && (
                <Bleed
                  marginBlock="250"
                  marginInlineStart="100"
                  marginInlineEnd="250"
                >
                  <IconButton
                    className={s.closeButton}
                    icon="close"
                    label="Close"
                    onClick={handleClose}
                    size="sm"
                    variant="tertiary"
                  />
                </Bleed>
              )}
            </Flex>
          )}
        </Flex>
      </div>
    </div>
  );
};
